// Ce fichier est généré automatiquement par scripts/generate-router-plugin.js
// Ne pas modifier manuellement - vos changements seront écrasés
// Pour régénérer : pnpm run generate:router-plugin

import { createRouter, createWebHistory } from "vue-router"

import { useAuthStore } from "@core/stores/auth.store"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"
import { isRouteAllowedWhenSubscriptionCancelled } from "@core/router/subscriptionCancelledGuard"

// ================================
// IMPORT DES TABLEAUX DE ROUTES
// ================================
import { routes as coreRoutes } from "@core/routes"
import { routes as abonnementRoutes } from "@modules/abonnement/routes"
import { routes as courtageRoutes } from "@modules/courtage/routes"
import { routes as dataRoutes } from "@modules/data/routes"
import { routes as energyManagerRoutes } from "@modules/energy-manager/routes"
import { routes as espaceEntrepriseRoutes } from "@modules/espace-entreprise/routes"
import { routes as homeRoutes } from "@modules/home/routes"
import { routes as irveRoutes } from "@modules/irve/routes"
import { routes as pvRoutes } from "@modules/pv/routes"
import { routes as usageProfileRoutes } from "@modules/usage-profile/routes"

// ================================
// CONSTRUCTION DU TABLEAU GLOBAL
// ================================
const coreRoot = coreRoutes[0]
const hasLayoutRoot = coreRoutes.length === 1 && coreRoot?.children != null
let routes
if (hasLayoutRoot) {
    const normalizeChildPath = (r: { path: string }) => ({
        ...r,
        path: r.path.startsWith("/") ? r.path.slice(1) : r.path,
    })
    const otherChildren = [
        abonnementRoutes,
        courtageRoutes,
        dataRoutes,
        energyManagerRoutes,
        espaceEntrepriseRoutes,
        homeRoutes,
        irveRoutes,
        pvRoutes,
        usageProfileRoutes,
    ]
        .flat()
        .map(normalizeChildPath)
    coreRoot.children = [...(coreRoot.children || []), ...otherChildren]
    routes = [coreRoot]
} else {
    routes = [
        ...coreRoutes,
        ...abonnementRoutes,
        ...courtageRoutes,
        ...dataRoutes,
        ...energyManagerRoutes,
        ...espaceEntrepriseRoutes,
        ...homeRoutes,
        ...irveRoutes,
        ...pvRoutes,
        ...usageProfileRoutes,
    ]
}

// Vérifie si 2 routes ont le même path et affiche un avertissement dans la console
const pathSet = new Set<string>()
function checkPathConflict(r: { path?: string }, prefix = "") {
    const p =
        (prefix
            ? prefix + (r.path?.startsWith("/") ? r.path : "/" + r.path)
            : r.path) || ""
    if (pathSet.has(p)) {
        console.error(
            '[router.plugin.ts] Conflit de route détecté : le path "' +
                p +
                '" est défini plusieurs fois.'
        )
    } else if (p) pathSet.add(p)
    if (Array.isArray(r.children))
        r.children.forEach((c) => checkPathConflict(c, p))
}
routes.forEach((r) => checkPathConflict(r))

const router = createRouter({
    history: createWebHistory(),
    routes,
})

router.beforeEach(async (to, _, next) => {
    // ✅ works because the pinia instance is now active
    const authStore = useAuthStore()

    if (authStore.isLogged) {
        if (!authStore.userId) {
            try {
                await authStore.fetchUser()
            } catch {
                next()
                return
            }
        }
        // Les interrupteurs produit se lisent UNE fois, la société connue
        // (DEV-1508 lot 3). `charger()` ne rejette jamais : une lecture ratée
        // laisse tout désarmé, c'est-à-dire l'état d'avant.
        if (authStore.userId) {
            await useInterrupteursStore().charger()
        }
        if (
            authStore.userId &&
            authStore.isClientHomeys &&
            authStore.isClientCancelled &&
            !isRouteAllowedWhenSubscriptionCancelled(to)
        ) {
            next({ name: "PageSubscriptionCancelled" })
            return
        }
    }

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (authStore.isLogged) {
            next()
        } else {
            // on enregistre la route précédente pour rediriger l'utilisateur après la connexion
            authStore.previousRoute = to.fullPath
            next({ name: "PageLogin" })
        }
    } else {
        next()
    }
})

export default router
